import React, { useMemo, useState } from 'react';
import { AvTooltip, Illustration, NoDataFound } from '@fabric-ui/components';
import { ActionButton, expandArrowColumn, FormatterType } from '@fabric-ui/components/Table';
import { Copy, Export } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button, capitalize, Collapse } from '@mui/material';
import Typography from '@mui/material/Typography';
import { exportToCSV } from '@utils/exportCSV.utils';
import { useSnackbar } from 'notistack';
import ReactDiffViewer, { DiffMethod, ReactDiffViewerStylesOverride } from 'react-diff-viewer';
import AvTable from '../../components/Table/AvTable';
import { useAvContext } from '../../context/AvContextProvider';
import { useGetUsersInfo } from '../../hooks/useGetUsersInfo';
import { buildPagination, useAuditLogs, useAuditLogsByAccount } from './hooks';
import { AuditEntityType, AuditEntry } from './types';

const copyIcon = <Copy style={{ width: 20, height: 20 }} />;

interface AuditLogsProps {
  id?: string;
  entityType: AuditEntityType;
  title: React.ReactNode;
  isQueryLoading?: boolean;
  defaultData?: AuditEntry[];
  codeMode?: boolean;
  pageSize?: number;
}

const stringifyForDiff = (data: object) =>
  JSON.stringify(data || {}, null, 2).replace(/^(\s*).*\\n.*$/gm, (line, indent) => line.replace(/\\n/g, `\n${indent}`));

export const PREVIOUS_VERSION_UNAVAILABLE_MESSAGE = "Previous version is older than 90 days and isn't shown";

export const isMissingPreviousVersion = ({ operation, oldRowData }: Pick<AuditEntry, 'operation' | 'oldRowData'>) =>
  operation === 'UPDATE' && !oldRowData;

export const MissingPreviousVersion: React.FC<{ newRowData: object }> = ({ newRowData }) => (
  <Box sx={{ ...flex.col, gap: 1 }}>
    <Typography variant="body2" sx={{ color: 'var(--color-content-base-tertiary)' }}>
      {PREVIOUS_VERSION_UNAVAILABLE_MESSAGE}
    </Typography>
    <Box component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--color-content-base-primary)' }}>
      {stringifyForDiff(newRowData)}
    </Box>
  </Box>
);

const AuditLogs: React.FC<AuditLogsProps> = ({ id, entityType, title, isQueryLoading, defaultData, codeMode = false, pageSize }) => {
  const { isLoading } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const [tablePage, setTablePage] = useState(0);
  const pageVars = useMemo(() => buildPagination({ page: tablePage, pageSize }), [tablePage]);
  const onError = error => enqueueSnackbar(error.message, { variant: 'error' });

  const byEntityResult = useAuditLogs({ id, entityType, pageVars, onError });
  const byAccountResult = useAuditLogsByAccount({ entityType, pageVars, onError, enabled: !id });
  const {
    data: { rowsFromQuery = [], totalElements } = { rowsFromQuery: [], totalElements: 0 },
    isLoading: isLogsLoading,
    isError: logsError,
  } = id ? byEntityResult : byAccountResult;
  const rows = defaultData || rowsFromQuery;
  const { usersInfo: auditUsers } = useGetUsersInfo(rows.map(({ actorId }) => actorId));
  const rowsWithActorName = rows.map(row => ({ ...row, actorName: auditUsers[row.actorId!] }));
  const loading = isLoading || isLogsLoading || isQueryLoading;
  const shouldPresentRuleNameCol = [AuditEntityType.AUTOMATIONS, AuditEntityType.GROUPING_RULE_SET].includes(entityType);
  const diffViewerStyle: ReactDiffViewerStylesOverride = {
    variables: {
      light: {
        codeFoldGutterBackground: 'var(--color-surface-base-secondary)',
        codeFoldBackground: 'var(--color-surface-base-secondary)',
        diffViewerBackground: 'transparent',
        codeFoldContentColor: 'var(--color-content-base-primary)',
        removedBackground: 'transparent',
        addedBackground: 'transparent',
      },
    },
    wordDiff: {
      borderRadius: '8px',
      paddingLeft: '8px',
      textIndent: '-6px',
    },
    diffAdded: {
      background: 'var(--color-surface-accent-teal-primary)',
      color: 'var(--color-content-base-primary)',
      pre: {
        color: 'var(--color-content-base-primary)',
      },
    },
    wordAdded: {
      background: 'var(--color-surface-accent-teal-primary)',
      color: 'var(--color-content-base-primary)',
    },
    wordRemoved: {
      background: 'var(--color-surface-accent-pink-primary)',
      color: 'var(--color-content-base-primary)',
    },
    diffRemoved: {
      background: 'var(--color-surface-accent-pink-primary)',
      color: 'var(--color-content-base-primary)',
      pre: {
        color: 'var(--color-content-base-primary)',
      },
    },
    line: {
      color: 'var(--color-content-base-primary)',
    },
    contentText: {
      color: 'var(--color-content-base-primary)',
    },
  };

  const codeFoldMessage = (totalFoldedLines: number) => (
    <Box
      sx={{
        '&.MuiTypography-root:hover': {
          color: 'var(--color-content-interactive-primary-default)',
        },
      }}>
      Expend {totalFoldedLines} Rows...
    </Box>
  );
  const expandedRow = (row: AuditEntry, onChange, isItemSelected) => (
    <Collapse in={isItemSelected} timeout="auto" unmountOnExit>
      <Box sx={{ padding: 2 }}>
        {isMissingPreviousVersion(row) ? (
          <MissingPreviousVersion newRowData={row.newRowData} />
        ) : (
          <ReactDiffViewer
            oldValue={stringifyForDiff(row.oldRowData)}
            newValue={stringifyForDiff(row.newRowData)}
            splitView={codeMode}
            hideLineNumbers={!codeMode}
            showDiffOnly={!codeMode}
            compareMethod={DiffMethod.LINES}
            extraLinesSurroundingDiff={0}
            styles={diffViewerStyle}
            codeFoldMessageRenderer={codeFoldMessage}
          />
        )}
      </Box>
    </Collapse>
  );

  const copyOldDataInlineAction = ({ oldRowData }) =>
    ActionButton(
      'Copy Previous Version',
      () => {
        navigator.clipboard.writeText(JSON.stringify(oldRowData, null, 2));
        enqueueSnackbar(`Previous Version Copied To Clipboard`, { variant: 'success' });
      },
      copyIcon
    );

  const headCells = shouldPresentRuleNameCol
    ? [...headCellsDefaults, { id: 'ruleName', label: 'Rule Name', getValue: row => row.newRowData.name || '' }]
    : headCellsDefaults;
  const exportTooltipText =
    rowsWithActorName.length > 0
      ? 'Download all visible audit logs, limited to the past 90 days'
      : 'No audit logs available from the past 90 days';
  return (
    <Box sx={{ backgroundColor: 'var(--color-background-primary)', ...flex.col, gap: 3 }}>
      <Box sx={{ ...flex.justifyBetweenCenter, h3: { margin: 0 } }}>
        <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
          {title && (
            <Typography variant="h3" sx={{ ...flex.itemsCenter, gap: 2, whiteSpace: 'pre', margin: 0 }}>
              {title}
            </Typography>
          )}
        </Box>
        <AvTooltip title={exportTooltipText}>
          <Button
            sx={{ p: 1 }}
            variant="filter"
            className="transparent"
            onClick={
              rowsWithActorName.length > 0 ? () => exportToCSV(rows, { propertiesToStringify: ['oldRowData', 'newRowData'] }) : undefined
            }>
            <Export />
          </Button>
        </AvTooltip>
      </Box>
      {rowsWithActorName.length === 0 && !loading ? (
        <NoDataFound img={<Illustration.Medium.NoData />} headline="No audit logs available from the past 90 days" />
      ) : (
        <AvTable
          resizable
          rows={rowsWithActorName}
          loading={!logsError && loading}
          pagination
          paginationProps={
            defaultData ? undefined : { onPageChange: page => setTablePage(+page), totalRows: totalElements, pageNum: tablePage }
          }
          headCells={headCells}
          enableSelectionColor={false}
          expandedRowObj={{ component: expandedRow }}
          allowEdit={false}
          allowKeyboardNavigation
          inlineActions={[copyOldDataInlineAction]}
          sx={{
            '.MuiTableCell-head': {
              backgroundColor: 'var(--color-background-primary)',
            },
            '.expanded-row, .expanded-row-content, .expanded-row-content .MuiTableCell-head': {
              backgroundColor: 'var(--color-surface-base-secondary)',
            },
            '.expanded-row-content > .MuiTableCell-root > div': {
              borderTop: '1px solid var(--color-border-base-secondary)',
            },
          }}
        />
      )}
    </Box>
  );
};

export default AuditLogs;

const headCellsDefaults = [
  {
    id: 'entryId',
    label: 'entryId',
    isKey: true,
    hidden: true,
  },
  expandArrowColumn(() => true),
  {
    id: 'dmlTimestamp',
    label: 'Time',
    type: FormatterType.date,
  },
  {
    id: 'operation',
    label: 'Operation',
    formatter: value => capitalize(value.toLowerCase()),
  },
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'actorName',
    label: 'User Name',
  },
];
